import { languageController } from "../controllers/language/languageController";
import { localStorageKeys } from "./constants";
import { getEndpointUrl } from "./endPoints";
import { getItemOnLocalStorage, setItemOnLocalStorage } from "./helpers";
import { handleRequest } from "./httpRequest";
import { Location } from '../types';

// Dealer geolocation interface
interface DealerGeolocation {
  latitude: number;
  longitude: number;
}

// Dealer interface for filtering
interface DealerWithGeolocation {
  geolocation: DealerGeolocation;
  [key: string]: any;
}

// Map bounds interface
interface MapBounds {
  minLat: number;
  maxLat: number;
  minLong: number;
  maxLong: number;
}

// Google Maps object interface (simplified)
interface GoogleMap {
  getBounds(): {
    getSouthWest(): { lat(): number; lng(): number };
    getNorthEast(): { lat(): number; lng(): number };
  };
}

// Geolocation API response
interface GeolocationAPIResponse {
  lat: number;
  long: number;
}

// User location result
interface UserLocationResult {
  success: boolean;
  data: Location | string;
}

// User location storage type
type LocationStorageType = 'geolocation' | 'postalcode';

// Previous location result
interface PreviousUserLocation {
  type: LocationStorageType;
  value: Location | string;
}

// Return dealers only in the current viewport/bounds
export const getDealersInViewport = (
  dealers: DealerWithGeolocation[],
  currentBounds?: MapBounds
): DealerWithGeolocation[] => {
  if (currentBounds) {
    const { minLat, maxLat, minLong, maxLong } = currentBounds;
    const filterdDealers = dealers.filter(({ geolocation }) => {
      return (
        (geolocation.latitude > minLat) &&
        (geolocation.latitude < maxLat) &&
        (geolocation.longitude > minLong) &&
        (geolocation.longitude < maxLong)
      );
    });
    return filterdDealers;
  }

  return dealers;
};

// Return current bounds' min lat/lng and max lat/lng
export const getCurrentBounds = (map: GoogleMap): MapBounds => {
  const bounds = map.getBounds();
  return ({
    minLat: bounds.getSouthWest().lat(),
    maxLat: bounds.getNorthEast().lat(),
    minLong: bounds.getSouthWest().lng(),
    maxLong: bounds.getNorthEast().lng(),
  });
};

// Convert postalcode to coordinates (lat and lng)
export const convertPostalToCoordinates = async (postalCode: string): Promise<Location | null> => {
  try {
    const coordinates = await handleRequest<GeolocationAPIResponse>(
      'get',
      `${getEndpointUrl('geolocation')}?postcode=${postalCode}`
    );
    if (coordinates) {
      return {
        lat: coordinates.lat,
        lng: coordinates.long
      };
    }
    return null;
  } catch (err) {
    console.log(err);
    return null;
  }
};

// Return current position if permitted
function getUserCurrentPosition(options: PositionOptions = {}): Promise<GeolocationPosition> {
  return new Promise((resolve, reject) =>
    navigator.geolocation.getCurrentPosition(resolve, reject, options)
  );
}

// Return user's geolocation using Geolocation API
export const getUserGeoLocation = async (): Promise<UserLocationResult> => {
  if (navigator.geolocation) {
    try {
      const position = await getUserCurrentPosition();
      return {
        success: true,
        data: {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        }
      };
    } catch (err: any) {
      let errText = '';
      console.log(err);
      switch (err.code) {
        case err.PERMISSION_DENIED: // 1
          errText = languageController.getCopy('findDealer', 'geoDenied') || 'Location access denied';
          break;
        case err.POSITION_UNAVAILABLE: // 2
          errText = languageController.getCopy('findDealer', 'geoUnavailable') || 'Location unavailable';
          break;
        case err.TIMEOUT: // 3
          errText = languageController.getCopy('findDealer', 'geoTimeout') || 'Location request timed out';
          break;
        case err.UNKNOWN_ERROR:
          errText = languageController.getCopy('findDealer', 'geoUnavailable') || 'Location unavailable';
          break;
        default:
          errText = languageController.getCopy('findDealer', 'geoUnavailable') || 'Location unavailable';
      }

      return {
        success: false,
        data: errText
      };
    }
  } else {
    return {
      success: false,
      data: languageController.getCopy('findDealer', 'geoUnavailable') || 'Geolocation is not supported'
    };
  }
};

// Return link for google direction
export const generateGoogleDirection = (from: Location, to: Location): string => {
  if (!from || !to) {
    console.error('Error: "from" or "to" is undefined in generateGoogleDirection');
    return '';
  }
  return `https://www.google.com/maps/dir/${from.lat},${from.lng}/${to.lat},${to.lng}`;
};

// Store user location data in localstorage.
export const storeUserLocation = (type: LocationStorageType, value: Location | string): void => {
  if (type === 'geolocation') {
    setItemOnLocalStorage(localStorageKeys.USER_GEO_LOCATION, value);
  } else if (type === 'postalcode') {
    setItemOnLocalStorage(localStorageKeys.USER_POSTALCODE, value);
  }
};

// Get a location that user has set previously on the site.
export const getPreviousUserlocation = (): PreviousUserLocation | null => {
  const geolocation = getItemOnLocalStorage<Location>(localStorageKeys.USER_GEO_LOCATION);
  if (geolocation) {
    return {
      type: 'geolocation',
      value: geolocation
    };
  }

  const postalcode = getItemOnLocalStorage<string>(localStorageKeys.USER_POSTALCODE);
  if (postalcode) {
    return {
      type: 'postalcode',
      value: postalcode
    };
  }

  return null;
};
